import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Divider, TextField } from '@mui/material'
import { Client } from '@stomp/stompjs'
import WebService from '@util/webService'
import useAlert from '@hooks/useAlert'
import ConfirmDialog from '@components/ConfirmDialog'
import axios from 'axios'
import moment from 'moment'

const pageSize = 30
const dateFormat = 'YYYY-MM-DD HH:mm:ss'
const CHAT_BG = '#eee'
const AVATAR = 40
const MAX_FILES = 5
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']
const REPO_URL = '/scm/repo'
const toTime = s => {
  const m = moment(s, dateFormat)
  const h = m.hour()
  return `${h < 12 ? '오전' : '오후'} ${h % 12 === 0 ? 12 : h % 12}:${m.format('mm')}`
}

const toDateLabel = s => {
  const m = moment(s, dateFormat)
  return `${m.year()}년 ${m.month() + 1}월 ${m.date()}일 ${WEEKDAYS[m.day()]}요일`
}

// 같은 사람 + 같은 분(YYYY-MM-DD HH:mm)이면 한 그룹
const sameGroup = (a, b) => !!a && !!b && a.userId === b.userId && a.regTime.substring(0, 16) === b.regTime.substring(0, 16)
const sameDay = (a, b) => !!a && !!b && a.regTime.substring(0, 10) === b.regTime.substring(0, 10)

// 최신순(id desc) 목록 두 개를 id 기준으로 합침: newer가 앞, older 중 중복 제외
const mergeById = (newer, older) => {
  const ids = new Set(newer.map(c => c.id))
  return [...newer, ...older.filter(c => !ids.has(c.id))]
}

// 첨부마다 표시용 주소(url)를 붙임
const withUrls = list => list.map(r => ({
  ...r,
  attchs: (r.attchs ?? []).map(a => ({ ...a, url: `${REPO_URL}/${a.fileLoc}` })),
}))

const isImageAttch = a => /\.(jpg|jpeg|png|gif|webp)$/i.test(a.filename ?? a.fileLoc ?? '')

// 응답의 읽음 위치(문자열)를 숫자 상태로 변환
const toReadState = report => ({
  ownerId: Number(report.ownerId),
  ownerLastReadId: Number(report.ownerLastReadId),
  staffLastReadId: Number(report.staffLastReadId),
})

// 응답과 read 신호의 도착 순서가 뒤섞여도 읽음 위치가 뒤로 가지 않게 큰 값을 유지
const mergeReadState = (prev, next) => ({
  ownerId: next.ownerId,
  ownerLastReadId: Math.max(prev.ownerLastReadId, next.ownerLastReadId),
  staffLastReadId: Math.max(prev.staffLastReadId, next.staffLastReadId),
})

const cs = {
  list: {
    height: '60vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column-reverse',   // 첫 항목(최신)이 맨 아래, 스크롤 시작 위치도 맨 아래
    backgroundColor: CHAT_BG,
    padding: '8px 0',
  },
  row: { display: 'flex', flexDirection: 'row', padding: '0 10px', marginTop: 4 },
  rowMine: { justifyContent: 'flex-end', alignItems: 'flex-end' },
  groupGap: { marginTop: 12 },
  metaMine: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  unread: { fontSize: 11, fontWeight: 'bold', color: '#e6a800', margin: '0 6px' },
  mineBody: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '65%' },
  avatarSlot: { width: AVATAR, marginRight: 8, flexShrink: 0 },
  avatar: {
    width: AVATAR,
    height: AVATAR,
    borderRadius: 16,
    backgroundColor: '#9fb3c5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  otherBody: { flex: 1, minWidth: 0 },
  otherContent: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '65%' },
  name: { fontSize: 13, color: '#3d4a55', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  bubbleLine: { display: 'flex', flexDirection: 'row', alignItems: 'flex-end' },
  bubble: {
    maxWidth: '100%',
    padding: '8px 12px',
    borderRadius: 14,
    backgroundColor: '#fff',
    border: '1px solid #dcdfe3',
    fontSize: 15,
    lineHeight: '21px',
    color: '#191919',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  attchWrap: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 },
  // 한 장: 원본 비율 그대로 (img는 width/height를 고정하지 않으면 잘리지 않음)
  thumbOne: { display: 'block', maxWidth: 240, maxHeight: 320, borderRadius: 10, backgroundColor: '#ddd', cursor: 'pointer' },
  // 여러 장: 정사각형 칸 안에 전체가 보이도록 contain
  thumb: { display: 'block', width: 110, height: 110, objectFit: 'contain', borderRadius: 10, backgroundColor: '#ddd', cursor: 'pointer' },
  fileChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: 240,
    padding: '6px 10px',
    borderRadius: 10,
    border: '1px solid #dcdfe3',
    backgroundColor: '#fff',
    color: '#191919',
    fontSize: 13,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  time: { fontSize: 11, color: '#8a9099', margin: '0 6px', whiteSpace: 'nowrap' },
  dateWrap: { display: 'flex', justifyContent: 'center', margin: '12px 0' },
  dateText: {
    padding: '4px 12px',
    borderRadius: 12,
    backgroundColor: '#e3e5e9',
    fontSize: 12,
    color: '#666',
  },
  inputWrap: { padding: '6px 8px', backgroundColor: CHAT_BG },
  inputBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 6,
  },
  // 파일을 끌고 들어왔을 때 대화 창 전체에 덮는 안내
  dropOverlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #1aa95d',
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.85)',
    color: '#3d4a55',
    fontSize: 15,
    pointerEvents: 'none',
  },
  deleted: { fontSize: 14, color: '#999', fontStyle: 'italic' },
  // 보낸 뒤 삭제 가능 시간 동안 보이는 삭제 버튼
  clearBtn: {
    border: 'none',
    background: 'none',
    padding: '0 6px',
    fontSize: 11,
    color: '#8a9099',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  plus: {
    width: 36,
    height: 36,
    borderRadius: 18,
    border: 'none',
    backgroundColor: '#f2f3f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
}

// 모바일(Ionicons person)과 같은 모양의 사람 아이콘. 아이콘 패키지 없이 SVG로 그림
const PersonIcon = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
    <circle cx="12" cy="8" r="4.2" />
    <path d="M4 20.2c0-4 3.6-6.4 8-6.4s8 2.4 8 6.4c0 .7-.5 1.2-1.2 1.2H5.2c-.7 0-1.2-.5-1.2-1.2z" />
  </svg>
)

// 모바일 입력창의 + 버튼과 같은 모양
const PlusIcon = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#191919" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const ClipIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.3 3.3 0 0 1 4.7 4.7l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9" />
  </svg>
)

// 이미지는 썸네일(누르면 새 탭에서 원본), 그 외 파일은 이름(누르면 다운로드)
const Attachments = ({ attchs, align }) => {
  const imageCount = attchs.filter(isImageAttch).length
  return (
    <div style={{ ...cs.attchWrap, justifyContent: align }}>
      {attchs.map(a => isImageAttch(a) ? (
        <img
          key={a.id}
          src={a.url}
          alt={a.filename}
          style={imageCount === 1 ? cs.thumbOne : cs.thumb}
          onClick={() => window.open(a.url, '_blank', 'noopener')}
        />
      ) : (
        <a key={a.id} href={a.url} download={a.filename} target="_blank" rel="noopener noreferrer" style={cs.fileChip} title={a.filename}>
          <ClipIcon />
          {a.filename}
        </a>
      ))}
    </div>
  )
}

const MessageRow = ({ item, upper, lower, myId, otherLastReadId, canClear, onClear, clockOffset, clearDuration }) => {
  const isMine = item.userId === myId
  const isGroupStart = !sameGroup(item, upper)
  const isGroupEnd = !sameGroup(item, lower)
  const showDate = !sameDay(item, upper)
  const hasAttchs = item.attchs?.length > 0
  const isCleared = item.delSn > 0

  // 삭제 가능 시간 동안만 삭제 버튼을 보이고, 시간이 지나면 스스로 숨김
  const remainMs = isMine && !isCleared
    ? Number(item.regMs) + clearDuration - (Date.now() + clockOffset)
    : 0
  const [clearable, set_clearable] = useState(remainMs > 0)
  useEffect(() => {
    if(remainMs <= 0) {
      set_clearable(false)
      return
    }
    set_clearable(true)
    const timer = setTimeout(() => set_clearable(false), remainMs)
    return () => clearTimeout(timer)
  }, [item.id, item.delSn, clockOffset, clearDuration])

  return (
    <div>
      {showDate && (
        <div style={cs.dateWrap}>
          <span style={cs.dateText}>{toDateLabel(item.regTime)}</span>
        </div>
      )}

      {isMine ? (
        <div style={{ ...cs.row, ...cs.rowMine, ...(isGroupStart ? cs.groupGap : {}) }}>
          {clearable && (
            <ConfirmDialog
              title="메시지 삭제"
              bc="error"
              okText="삭제"
              cancelText="취소"
              content="모든 대화 상대에게서 삭제합니다."
              preHandle={() => canClear(item)}
              perform={() => onClear(item)}
              btn={
                <button type="button" style={cs.clearBtn}>
                  삭제
                </button>
              }
            />
          )}
          <div style={cs.metaMine}>
            {item.id > otherLastReadId && <span style={cs.unread}>1</span>}
            {isGroupEnd && <span style={cs.time}>{toTime(item.regTime)}</span>}
          </div>
          <div style={cs.mineBody}>
            {isCleared ? (
              <div style={cs.bubble}><span style={cs.deleted}>{item.content}</span></div>
            ) : (
              <>
                {hasAttchs && <Attachments attchs={item.attchs} align="flex-end" />}
                {!!item.content && <div style={cs.bubble}>{item.content}</div>}
              </>
            )}
          </div>
        </div>
      ) : (
        <div style={{ ...cs.row, ...(isGroupStart ? cs.groupGap : {}) }}>
          <div style={cs.avatarSlot}>
            {isGroupStart && <div style={cs.avatar}><PersonIcon /></div>}
          </div>
          <div style={cs.otherBody}>
            {isGroupStart && !!item.userAlias && (
              <div style={cs.name} title={item.userAlias}>{item.userAlias}</div>
            )}
            <div style={cs.bubbleLine}>
              <div style={cs.otherContent}>
                {isCleared ? (
                  <div style={cs.bubble}><span style={cs.deleted}>{item.content}</span></div>
                ) : (
                  <>
                    {hasAttchs && <Attachments attchs={item.attchs} align="flex-start" />}
                    {!!item.content && <div style={cs.bubble}>{item.content}</div>}
                  </>
                )}
              </div>
              {isGroupEnd && <span style={cs.time}>{toTime(item.regTime)}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const ManagerChatPartDialog = ({ record, dialogHandler, callback, setCloseHandler }) => {
  const { alert, renderAlert } = useAlert()
  const schola = useSelector(state => state.scholaReducer)
  const account = useSelector(state => state.accountReducer)
  const myId = account?.user?.id

  const [dataList, set_dataList] = useState([])
  const [content, set_content] = useState('')
  const [sending, set_sending] = useState(false)
  const [brokerUrl, set_brokerUrl] = useState(null)
  const [readState, set_readState] = useState({ ownerId: -1, ownerLastReadId: 0, staffLastReadId: 0 })
  const pageNumRef = useRef(0)
  const totalCountRef = useRef(0)
  const loadingRef = useRef(false)
  const scrollRef = useRef(null)
  const sentinelRef = useRef(null)
  const fileInputRef = useRef(null)
  const dragDepthRef = useRef(0)
  const plusBtnRef = useRef(null)
  // 끌어다 놓은 파일: + 버튼을 대신 눌러 확인 창을 열 때 preHandle이 파일 선택 창 대신 사용
  const droppedRef = useRef(null)
  // 확인 창에 표시하고 전송할 파일
  const pickedRef = useRef([])
  const [dragging, set_dragging] = useState(false)
  // 상대방: 내가 방 주인이면 매니저 쪽, 아니면 방 주인
  const otherLastReadId = myId === readState.ownerId ? readState.staffLastReadId : readState.ownerLastReadId
  // 서버 시계와의 차이, 삭제 가능 시간 (getSubs 응답에서 받음)
  const clockOffsetRef = useRef(0)
  const clearDurationRef = useRef(0)

  // ConfirmDialog의 preHandle: 시간이 지났으면 안내만 하고 확인 창을 열지 않음
  const canClear = item => {
    const elapsed = Date.now() + clockOffsetRef.current - Number(item.regMs)
    if(item.delSn > 0 || elapsed >= clearDurationRef.current) {
      alert('보낸 지 5분이 지난 메시지는 삭제할 수 없습니다.')
      return false
    }
    return true
  }

  // 내 화면과 신호를 받은 상대 화면에서 공통으로 쓰는 "삭제됨" 반영 (서버 응답과 같은 문구)
  const markCleared = chatId => {
    set_dataList(prev => prev.map(c => c.id === chatId ? { ...c, delSn: 1, content: '삭제된 메시지입니다.', attchs: [] } : c))
  }

  const clearChat = async item => {
    try {
      const resp = await axios.post(`/chat/clear/${item.id}/school/${schola.id}`)
      if(resp.data.repCode !== 'ack') {
        alert(resp.data.repMessage)
        return
      }
      markCleared(item.id)
    }
    catch(e) {
      console.log('chat clear failed =>', e)
      alert('삭제하지 못했습니다.')
    }
  }

  const fetchChats = async (pageNum) => {
    const url = `/chat/sub/${record.id}/school/${schola.id}`
    const resp = await WebService.get(url, { pageNum, pageSize })
    if(resp.repCode !== 'ack') {
      alert(resp.repMessage)
      return null
    }
    return JSON.parse(resp.repMessage)
  }

  const handleClose = () => {
    dialogHandler[1](false)
    callback?.()
  }

  const onLoad = async (fresh) => {
    if(loadingRef.current) return
    if(!fresh && pageNumRef.current > 0 && pageSize * pageNumRef.current >= totalCountRef.current) return

    loadingRef.current = true
    try {
      const pageNum = fresh ? 0 : pageNumRef.current
      const info = await fetchChats(pageNum)
      if(!info) return

      const records = withUrls(JSON.parse(info.records))
      totalCountRef.current = Number(info.totalCount)
      set_readState(prev => mergeReadState(prev, toReadState(info)))
      clockOffsetRef.current = Number(info.serverTime) - Date.now()
      clearDurationRef.current = Number(info.clearDuration)
      pageNumRef.current = pageNum + 1
      if(info.brokerRemoteHost) {
        set_brokerUrl(`wss://${info.brokerRemoteHost}/stomp`)
      }
      if(fresh) {
        set_dataList(records)
      }
      else {
        set_dataList(prev => mergeById(prev, records))
      }
    }
    finally {
      loadingRef.current = false
    }
  }

  // 메시지 전송 공통: 텍스트만, 또는 파일만(내용은 빈 문자열) 보냄. 성공하면 true
  const postChat = async (text, files) => {
    if(sending) return false

    const now = moment().format(dateFormat)
    const chat = {
      id: -1,
      rootId: record.id,
      schoolId: schola.id,
      userId: myId,
      userAlias: '',
      lectureId: -1,
      lastId: -1,
      catCode: 'C000',
      content: text,
      uptTime: now,
      regTime: now,
    }
    const formData = new FormData()
    files.forEach(f => formData.append('files', f, f.name))
    formData.append('body', new Blob([JSON.stringify(chat)], { type: 'application/json' }))

    set_sending(true)
    try {
      const resp = await axios.post(`/chat/school/${schola.id}`, formData, { params: { pageSize } })
      if(resp.data.repCode !== 'ack') {
        alert(resp.data.repMessage)
        return false
      }
      const info = JSON.parse(resp.data.repMessage)
      totalCountRef.current = Number(info.totalCount)
      set_dataList(prev => mergeById(withUrls(JSON.parse(info.records)), prev))
      set_readState(prev => mergeReadState(prev, toReadState(info)))
      // column-reverse에서는 scrollTop 0이 맨 아래
      if(scrollRef.current) {
        scrollRef.current.scrollTop = 0
      }
      return true
    }
    catch(e) {
      console.log('chat post failed =>', e)
      alert('전송하지 못했습니다.')
      return false
    }
    finally {
      set_sending(false)
    }
  }

  const onSendText = async () => {
    const text = content.trim()
    if(!text) return
    if(await postChat(text, [])) {
      set_content('')
    }
  }

  // 사진만 남기고 개수 확인. 쓸 수 없으면 안내 후 null
  // HEIC는 브라우저에 따라 type이 비어 있어 확장자로도 확인
  const validImages = fileList => {
    const all = Array.from(fileList ?? [])
    const files = all.filter(f => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name))
    if(files.length === 0) {
      if(all.length > 0) {
        alert('사진 파일만 첨부할 수 있습니다.')
      }
      return null
    }
    if(files.length > MAX_FILES) {
      alert(`최대 ${MAX_FILES}장까지만 첨부할 수 있습니다.`)
      return null
    }
    return files
  }

  // ConfirmDialog의 preHandle: 끌어다 놓은 파일이 있으면 그것을, 없으면 파일 선택 창에서 고른 것을 사용
  // true를 돌려줘야 확인 창이 열림
  const preparePicked = () => {
    if(droppedRef.current) {
      pickedRef.current = droppedRef.current
      droppedRef.current = null
      return Promise.resolve(true)
    }
    return new Promise(resolve => {
      const input = fileInputRef.current
      if(!input) {
        resolve(false)
        return
      }
      input.onchange = e => {
        const files = validImages(e.target.files)
        // 같은 파일을 다시 골라도 change가 발생하도록 초기화
        e.target.value = ''
        if(!files) {
          resolve(false)
          return
        }
        pickedRef.current = files
        resolve(true)
      }
      // 파일 선택 창에서 취소한 경우 (지원 브라우저에서만 발생)
      input.oncancel = () => resolve(false)
      input.click()
    })
  }

  // 확인 창에 보일 내용: 장수와 파일 이름 목록
  const pickedMessage = () => {
    const files = pickedRef.current
    if(files.length === 0) return ''
    return `사진 ${files.length}장을 전송하시겠습니까?\n\n${files.map(f => f.name).join('\n')}`
  }

  // 입력 중인 글은 그대로 두고 사진만 전송
  const sendPicked = async () => {
    const files = pickedRef.current
    pickedRef.current = []
    if(files.length > 0) {
      await postChat('', files)
    }
  }

  // 끌어다 놓기: 자식 요소를 지날 때마다 enter/leave가 반복되므로 깊이를 세어 안내 표시를 유지
  const hasFiles = e => Array.from(e.dataTransfer?.types ?? []).includes('Files')

  const onDragEnter = e => {
    if(!hasFiles(e)) return
    e.preventDefault()
    dragDepthRef.current += 1
    set_dragging(true)
  }

  const onDragOver = e => {
    if(!hasFiles(e)) return
    // 기본 동작(브라우저가 파일을 새 탭으로 열어 버림)을 막아야 drop이 발생함
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const onDragLeave = e => {
    if(!hasFiles(e)) return
    dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0)
    if(dragDepthRef.current === 0) {
      set_dragging(false)
    }
  }

  const onDrop = e => {
    if(!hasFiles(e)) return
    e.preventDefault()
    dragDepthRef.current = 0
    set_dragging(false)
    if(sending) return
    const files = validImages(e.dataTransfer.files)
    if(!files) return
    // + 버튼을 대신 눌러 ConfirmDialog를 엶 (preHandle이 droppedRef를 사용)
    droppedRef.current = files
    plusBtnRef.current?.click()
  }

  const onKeyDown = e => {
    // Enter 전송, Shift+Enter 줄바꿈. 한글 조합 중 Enter는 무시해야 두 번 전송되지 않음
    if(e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      onSendText()
    }
  }

  useEffect(() => {
    if(setCloseHandler) {
      setCloseHandler(handleClose)
    }
  }, [setCloseHandler])

  useEffect(() => {
    onLoad(true)
  }, [record.id])

  // 목록 맨 위의 sentinel이 보이면 이전 페이지 요청 (모바일의 onEndReached 역할)
  // 목록이 바뀔 때마다 다시 관찰해야, 내용이 짧아 sentinel이 계속 보이는 경우에도 이어서 불러옴
  useEffect(() => {
    const root = scrollRef.current
    const target = sentinelRef.current
    if(!root || !target) return

    const io = new IntersectionObserver(entries => {
      if(entries[0].isIntersecting) {
        onLoad(false)
      }
    }, { root, rootMargin: '100px 0px 0px 0px' })
    io.observe(target)
    return () => io.disconnect()
  }, [dataList])

  // STOMP 구독: 새 메시지 신호를 받으면 최신 페이지를 다시 조회해 합침
  useEffect(() => {
    if(!record.id || !brokerUrl) return

    // 백그라운드 갱신이므로 실패해도 alert 없이 넘어감
    const fetchLatest = async () => {
      const url = `/chat/sub/${record.id}/school/${schola.id}`
      const resp = await WebService.get(url, { pageNum: 0, pageSize })
      if(resp.repCode !== 'ack') return
      const info = JSON.parse(resp.repMessage)
      totalCountRef.current = Number(info.totalCount)
      set_dataList(prev => mergeById(withUrls(JSON.parse(info.records)), prev))
      set_readState(prev => mergeReadState(prev, toReadState(info)))
    }

    // 짧은 시간에 몰린 신호는 한 번의 조회로 묶음
    let timer = null
    const scheduleFetch = () => {
      if(timer) return
      timer = setTimeout(() => {
        timer = null
        fetchLatest()
      }, 300)
    }

    const client = new Client({
      brokerURL: brokerUrl,
      reconnectDelay: 5000,
      heartbeatIncoming: 20000,
      heartbeatOutgoing: 20000,
      onConnect: () => {
        client.subscribe(`chat.room.${record.id}`, frame => {
          const signal = JSON.parse(frame.body)
          const senderId = Number(signal.userId)
          if(senderId === myId) return

          // 상대방이 읽음: 조회 없이 읽음 위치만 올림
          if(signal.type === 'read') {
            const lastReadId = Number(signal.lastReadId)
            set_readState(prev => senderId === prev.ownerId
              ? { ...prev, ownerLastReadId: Math.max(prev.ownerLastReadId, lastReadId) }
              : { ...prev, staffLastReadId: Math.max(prev.staffLastReadId, lastReadId) })
            return
          }
          // 상대방이 메시지를 삭제함
          if(signal.type === 'clear') {
            markCleared(Number(signal.chatId))
            return
          }
          scheduleFetch()
        })
        // 첫 연결과 재연결 모두: 끊겨 있던 사이 놓친 메시지 보충
        scheduleFetch()
      },
      onStompError: frame => console.log('stomp error =>', frame.headers.message),
      onWebSocketClose: () => console.log('stomp websocket closed'),
    })
    client.activate()

    return () => {
      if(timer) clearTimeout(timer)
      client.deactivate()
    }
  }, [record.id, brokerUrl])

  return (
    <div
      style={{ position: 'relative' }}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {renderAlert()}
      {dragging && <div style={cs.dropOverlay}>여기에 사진을 놓으세요</div>}

      <div ref={scrollRef} style={cs.list}>
        {dataList.map((item, index) => (
          <MessageRow
            key={item.id}
            item={item}
            upper={dataList[index + 1]}
            lower={dataList[index - 1]}
            myId={myId}
            otherLastReadId={otherLastReadId}
            canClear={canClear}
            onClear={clearChat}
            clockOffset={clockOffsetRef.current}
            clearDuration={clearDurationRef.current}
          />
        ))}
        <div ref={sentinelRef} style={{ height: 1, flexShrink: 0 }} />
      </div>

      <div style={cs.inputWrap}>
        <div style={cs.inputBar}>
          <ConfirmDialog
            title="업로드"
            bc="primary"
            okText="전송"
            cancelText="취소"
            preHandle={preparePicked}
            content={pickedMessage}
            perform={sendPicked}
            btn={
              <button
                ref={plusBtnRef}
                type="button"
                style={{ ...cs.plus, opacity: sending ? 0.5 : 1 }}
                disabled={sending}
                title="사진 첨부"
              >
                <PlusIcon />
              </button>
            }
          />
          <input ref={fileInputRef} type="file" accept="image/*" multiple hidden />
          <TextField
            value={content}
            onChange={e => set_content(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="내용 입력"
            multiline
            maxRows={5}
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true, style: { fontSize: 15, paddingLeft: 6 } }}
          />
          <Button
            variant="contained"
            disableElevation
            disabled={!content.trim() || sending}
            onClick={onSendText}
            style={{ borderRadius: 18, minWidth: 64, flexShrink: 0 }}
          >
            전송
          </Button>
        </div>
      </div>

      <Divider />
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
        <Button onClick={() => handleClose()}>
          닫기
        </Button>
      </div>
    </div>
  )
}

export default ManagerChatPartDialog
